import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import { FirstBaggages } from '../../interface/first-baggages';
import { JourneyElements } from '../../interface/journey-elements';
import { Lounges } from '../../interface/lounges';
import {
  CprResultState,
  CprResultStore,
  resetCprResult,
  selectCprResult,
  selectCprResultIsFailureStatus,
  selectCprResultIsPendingStatus,
  selectCprResultState,
  setCprResultFromApi,
} from '../../store';
import { ErrorType } from '@lib/interfaces';
import { ApiErrorResponseService, ErrorsHandlerService, PageLoadingService } from '@lib/services';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import {
  JourneyElement,
  JourneyElementAcceptanceEligibility,
  PostJourneysGetJourneyRequest,
  PostJourneysGetJourneyResponse,
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInner,
} from 'src/sdk_checkin';
import { DefaultService as Api } from 'src/sdk_checkin/api/default.service';
import { CprResultWrapper } from './cpr-result-wrapper';
import { JuniorPilots } from '../../interface/junior-pilots';
import { isSuffixSegment } from '@common/helpers';

@Injectable()
export class CprResultStoreService extends CheckinBaseClass {
  private _cprResult$: Observable<CprResultState>;
  private _cprResultData!: CprResultState;
  private _journeyElementsByTraveler = new Map<string, JourneyElement[]>();
  get cprResultData() {
    return this._cprResultData;
  }

  /** APIエラーSubscription */
  apiErrorResponseSubscription?: Subscription;

  constructor(
    private _store: Store<CprResultStore>,
    private _api: Api,
    private _apiErrorResponseService: ApiErrorResponseService,
    private _errorsHandlerService: ErrorsHandlerService,
    private _pageLoadingService: PageLoadingService
  ) {
    super();
    this._cprResult$ = this._store.pipe(
      select(selectCprResultState),
      filter((data) => !!data && !!data.model)
    );
    this.subscribeService(this._cprResult$, (data) => {
      this._cprResultData = data;
      this.extractJourneyElementsByTraveler(data.model);
    });
  }

  /**
   * CPR情報から搭乗者IDをキー、搭乗者ごとのjourneyElement配列を値とするマップを作成する
   * @param cprResult
   * @returns
   */
  private extractJourneyElementsByTraveler(cprResult: PostJourneysGetJourneyResponse | null) {
    cprResult?.data.travelers.forEach((traveler) => {
      const journeyElements: JourneyElement[] = [];
      cprResult?.data.flights.forEach((flight) => {
        const journeyElementObj = cprResult?.data.journeyElements as JourneyElements;
        journeyElements.push(journeyElementObj[flight.id][traveler.id]);
      });
      this._journeyElementsByTraveler.set(traveler.id, journeyElements);
    });
  }

  /**
   * 搭乗者Idを指定し、搭乗者ごとのjourneyElement配列を返す
   * @param travelerId
   * @returns 指定された搭乗者のjourneyElement配列
   */
  public getJourneyElementsByTraveler(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements;
  }

  override destroy(): void {
    if (!!this.apiErrorResponseSubscription) {
      this.apiErrorResponseSubscription.unsubscribe();
      this.apiErrorResponseSubscription = undefined;
    }
  }

  /**
   * CprResultStoreのObservableを返却する
   * @returns CprResultStoreの全ての値を取得するObservable
   */
  public getCprResultState$() {
    return this._cprResult$;
  }

  /**
   * CprResultStoreのAPIのレスポンス部分のObservableを返却する
   * @returns CprResultStoreのレスポンス部分を取得するObservable
   */
  public getCprResult$() {
    return this._store.pipe(select(selectCprResult));
  }

  /**
   * CprResultStoreのAPIのレスポンス部分のObservableを返却する
   * チェックインでよく使う、搭乗者別での分類などのヘルパメソッドを併せ持ちます。
   * @returns CprResultStoreのレスポンス部分を取得するObservable
   */
  public getWrappedCprResult$(): Observable<CprResultWrapper> {
    return this._store.pipe(
      select(selectCprResult),
      filter((value) => Boolean(value)),
      map((value) => new CprResultWrapper(value!))
    );
  }

  /**
   * CprResultStoreがAPI呼び出し中かのObservableを返却する
   * @returns CprResultStoreのisPendingの値を取得するObservable
   */
  public getIsPending$() {
    return this._store.pipe(select(selectCprResultIsPendingStatus));
  }

  /**
   * CprResultStoreがAPI呼び出しが失敗したかのObservableを返却する
   * @returns CprResultStoreのisFailureの値を取得するObservable
   */
  public getIsFailure$() {
    return this._store.pipe(select(selectCprResultIsFailureStatus));
  }

  /**
   * API呼び出し結果のObservableを受け取りStoreに格納する
   * @param call API呼び出し結果を取得するObservable
   */
  public setCprResult(call: Observable<PostJourneysGetJourneyResponse>) {
    this._store.dispatch(setCprResultFromApi({ call }));
  }

  /**
   * CprResultStoreの中身をクリアする(初期状態に戻す)
   */
  public clearCprResult() {
    this._store.dispatch(resetCprResult());
  }

  /**
   * CPR取得共通処理
   *
   * CPR取得 APIの呼び出しと呼び出し結果のStoreへの設定を行い、
   * APIエラーを取得するための Observable を返す
   *
   * @param journeyId
   * @param selectedFlightIds
   * @param searchProperty
   */
  callApi$(param: PostJourneysGetJourneyRequest) {
    this.setupApiErrorHandler();
    const call = this._api.journeysGetJourneyPost(param);
    this.setCprResult(call);
  }

  /**
   * CPR情報取得APIのエラーを監視して処理する。
   */
  private setupApiErrorHandler() {
    if (!!this.apiErrorResponseSubscription) {
      return;
    }
    this.apiErrorResponseSubscription = this.getIsFailure$()
      .pipe(
        filter((isFailure) => isFailure),
        withLatestFrom(this._apiErrorResponseService.getApiErrorResponse$(), (_, state) => state),
        map((state) => {
          if (!!state && state.errors && state.errors.length > 0) {
            this._errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: state.errors[0].code,
            });
          }
          // ローディング画面表示終了
          this._pageLoadingService.endLoading(true);
        })
      )
      .subscribe();
  }

  /**
   * 搭乗者Idを指定しその搭乗者のjourneyElementの中に1便でもチェックイン状態のセグメントが含まれているかを返す
   * @param travelerId
   * @returns チェックイン済みセグメントが存在する場合true、それ以外の場合flase
   */
  public isIncludeCheckedinSegment(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements?.some((journeyElement) => this.isCheckedIn(journeyElement));
  }

  /**
   * 搭乗者Idを指定しその搭乗者のjourneyElementの中に1便でもチェックイン可能であるセグメントが含まれているかを返す
   * @param travelerId
   * @returns チェックイン可能であるセグメントが含まれていればtrue、そうでなければfalse
   */
  public isIncludeCanCheckinSegment(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements?.some(
      (journeyElement) =>
        journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.NotAccepted &&
        journeyElement.acceptanceEligibility.status === JourneyElementAcceptanceEligibility.StatusEnum.Eligible
    );
  }

  /**
   * 搭乗者Idを指定して、その搭乗者のacceptanceStatusSummaryを見て情報のチェックインに関わる情報の変更ができる状態かを判定する
   * @param travelerId
   * @returns acceptanceStatusSummaryがacceptedまたはPartialであればtrue、それ以外であればfalse
   */
  public canChangeCheckinInformationByAcceptanceStatus(travelerId: string) {
    const traveler = this._cprResultData.model?.data.travelers.filter((traveler) => traveler.id === travelerId)[0];
    return (
      traveler?.acceptanceStatusSummary ===
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Accepted ||
      traveler?.acceptanceStatusSummary ===
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Partial
    );
  }

  /**
   * 座席変更時間外のフライトかを判定する
   * @returns 全てのフライトについて座席変更時間外であればtrue、そうでなければfalse
   */
  public isNotShowChageSeatButton() {
    return this._cprResultData.model?.data.flights.every((flight) => !flight.isSeatChangeEligible);
  }

  /**
   * 搭乗者入力情報変更時間外かを判定する
   * @returns 搭乗者入力情報変更時間外であればtrue、そうでなければfalse
   */
  public isNotShowChangePassengerInputButton() {
    return !this._cprResultData.model?.data.isUpdateTravelersEligible;
  }

  /**
   * サービス変更時間外の搭乗者かを判定する
   * @returns サービス変更時間外の搭乗者であればtrue、そうでなければfalse
   */
  public isNotShowChangeServiceButton() {
    return !this._cprResultData.model?.data.isServiceApplicationEligible;
  }

  /**
   * journeyElement配列を確認し、同行者が存在するかどうかを判定する
   * @returns 全てのjourneyElementのorderIdを確認し、異なるorderIdを持つものが存在すればtrue、そうでなければfalse
   */
  public isIncludeCompanion() {
    const allJourneyElements = [...this._journeyElementsByTraveler.values()].flat();
    const orderIds = allJourneyElements?.map((journeyElement) => journeyElement?.orderId);
    const orderIdSet = new Set(orderIds);
    return orderIdSet.size > 1;
  }

  /**
   * 指定されたjourneyElementがチェックイン済みかどうかを判定する
   * @param journeyElement
   * @returns 指定されたjourneyElement.acceptanceStatusがacceptedかstandbyの場合true、それ以外はfalse
   */
  public isCheckedIn(journeyElement: JourneyElement) {
    return (
      journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.Accepted ||
      journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.StandBy
    );
  }

  /**
   * 指定された搭乗者についてサービスが申し込まれているかを判定するメソッド
   * @param travelerId サービスを申し込んでいる搭乗者のID
   * @returns サービスが1つでも申し込まれていればtrue、そうでなければfalse
   */
  public isAppliedServices(travelerId: string) {
    // 事前手荷物が申し込まれているかのフラグを作成
    const firstBaggages = this._cprResultData.model?.data.services?.baggage?.firstBaggage as FirstBaggages;
    const firstBaggageApplied = !!firstBaggages && !!firstBaggages[travelerId] ? true : false;

    // ラウンジが申し込まれているかのフラグを作成
    const lounges = this._cprResultData.model?.data.services?.lounge as Lounges;
    let isLoungeApplied = false;
    const flights = this._cprResultData.model?.data.flights;
    if (flights) {
      isLoungeApplied = flights.some((flight) => {
        const loungeByFlights = lounges ? lounges[flight.id] : undefined;
        if (!!loungeByFlights && !!loungeByFlights[travelerId]) {
          return true;
        }
        return false;
      });
    }

    // ジュニアパイロットが申し込まれているかのフラグを作成
    const juniorPilots = this._cprResultData.model?.data.services?.juniorPilot as JuniorPilots;
    let isJuniorPilotApplied = false;
    if (flights) {
      isJuniorPilotApplied = flights.some((flight) => {
        const juniorPilotByFlights = juniorPilots ? juniorPilots[flight.id] : undefined;
        if (!!juniorPilotByFlights && !!juniorPilotByFlights[travelerId]) {
          return true;
        }
        return false;
      });
    }

    // サービスが1件でも申し込まれているかどうかのフラグの作成
    return firstBaggageApplied || isLoungeApplied || isJuniorPilotApplied;
  }

  /**
   * 指定されたセグメント、搭乗者に紐づくjourneyElementからcabinクラスを重複を除いて返す
   * @param flights セグメントの配列、指定しない場合はCPRの全てのセグメント
   * @param traveler 搭乗者の配列、指定しない場合はCPRの全ての搭乗者
   * @returns 重複を除いたcabinクラスの配列
   */
  extractNonDuplicateCabinClassesBySegment(
    flight: PostJourneysGetJourneyResponseDataFlightsInner,
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[] = this._cprResultData.model?.data.travelers || []
  ): string[] {
    // journeyElementから選択されたセグメント、選択された搭乗者を元に、キャビンクラスを重複を取り除き抽出
    const cabinSet = new Set<string>();
    const journeyElements = this._cprResultData.model?.data.journeyElements as JourneyElements;
    travelers.forEach((passenger) => {
      const cabin = journeyElements[flight.id][passenger.id].cabin;

      if (cabin) {
        cabinSet.add(cabin);
      }
    });

    return Array.from(cabinSet);
  }

  /**
   * 指定されたセグメント、指定された搭乗者にてキャビンクラスが混在しているかを返す
   * キャビンクラス混在とは、指定されたセグメントを繰り返し、1つでもキャビンクラスが混在するようなセグメントが存在すれば混在と返す
   * @param flights 判定対象となるセグメント
   * @param travelers 判定対象となる搭乗者
   * @returns キャビンクラスが混在状態であればtrue、そうでなければfalse
   */
  isCabinClassMixed(
    flights: PostJourneysGetJourneyResponseDataFlightsInner[] = this._cprResultData.model?.data.flights || [],
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[] = this._cprResultData.model?.data.travelers || []
  ) {
    // セグメントごとに指定された搭乗者について重複なしでキャビンクラスを取り出す
    const cabinsBySegment = flights.map((flight) => {
      return this.extractNonDuplicateCabinClassesBySegment(flight, travelers);
    });

    // 各セグメントのキャビンクラスのうち、1セグメントでも要素数が2以上のものが存在すればキャビンクラス混在
    return cabinsBySegment.some((cabins) => cabins.length >= 2);
  }

  /**
   * CPRに含まれる全ての便がSuffix便であるかを判定する。
   * @returns 全便がSuffix便であればtrue、1便でもSuffix便でないものがあればfalse
   */
  isAllSuffixSegment() {
    return !!this.cprResultData.model?.data.flights.every((flight) =>
      isSuffixSegment(flight.marketingFlightNumber || '')
    );
  }
}
